import moment from "moment";

// Empty values print as a plain hyphen, never an em dash and never a word that
// asserts something about the chart ("None", "No known allergies"). A blank
// field on a transfer sheet has to read as "nobody recorded this".
export const EMPTY = "-";

export const NOT_DOCUMENTED = "Not documented";

export const CODE_STATUS_OPTIONS = ["Full code", "DNR / POLST", NOT_DOCUMENTED];

export const ISOLATION_OPTIONS = [
  "None in effect, standard precautions",
  "Contact",
  "Droplet",
  "Airborne",
  "Not assessed",
];

export const DIRECTIVE_LOCATION_OPTIONS = [
  "Original travels with client",
  "Chart, front cover",
  "Facility safe",
];

export const TRANSPORT_OPTIONS = ["Ambulance", "Staff vehicle", "Family"];

export const DEVICE_OPTIONS = [
  "Eyeglasses",
  "Dentures",
  "Hearing aid",
  "Walker or cane",
  "Wheelchair",
  "Oxygen",
];

export const ATTACHMENT_OPTIONS = [
  { key: "advancedDirectives", label: "DNR / POLST, original orange form" },
  { key: "faceSheet", label: "Face sheet" },
  { key: "hipaaRelease", label: "HIPAA release" },
  { key: "currentMar", label: "Current MAR, 30 day" },
  { key: "guardianshipOrder", label: "Guardianship order" },
];

export const VITAL_FIELDS = [
  {
    key: "bloodPressure",
    label: "Blood pressure",
    short: "BP",
    unit: "mmHg",
    placeholder: "148/92",
  },
  { key: "pulse", label: "Pulse", short: "Pulse", unit: "bpm", placeholder: "104" },
  {
    key: "respirations",
    label: "Respirations",
    short: "Resp",
    unit: "/min",
    placeholder: "22",
  },
  {
    key: "temperature",
    label: "Temperature",
    short: "Temp",
    unit: "F",
    placeholder: "99.1",
  },
  { key: "spo2", label: "SpO2", short: "SpO2", unit: "%", placeholder: "94" },
  {
    key: "glucose",
    label: "Blood glucose",
    short: "Glucose",
    unit: "mg/dL",
    placeholder: "248",
  },
];

export const TIME_FIELDS = [
  { key: "called911", label: "911 called" },
  { key: "emsArrived", label: "EMS arrived" },
  { key: "departedFacility", label: "Departed facility" },
  { key: "lastOralIntake", label: "Last oral intake" },
];

export const formatDate = (value) =>
  value && moment(value).isValid() ? moment(value).format("MM/DD/YYYY") : "";

// 12 hour with a meridiem, the same way the drawer's DateTimePicker and
// TimeInput render it. The sheet must not show one clock on screen and
// another on paper.
export const DATE_TIME_FORMAT = "MM/DD/YYYY hh:mm A";

export const formatDateTime = (value) =>
  value && moment(value).isValid() ? moment(value).format(DATE_TIME_FORMAT) : "";

export const TIME_FORMAT = "hh:mm A";

// "14:02" (the value a TimeInput hands back) rendered as "02:02 PM". Padded
// so the times row on the sheet stays column aligned.
export const formatClock = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm", "HH:mm:ss", "h:mm A", "hh:mm A"], true);
  return parsed.isValid() ? parsed.format(TIME_FORMAT) : `${value}`;
};

export const joinParts = (...parts) => parts.filter(Boolean).join(", ");

export const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "CL";

const age = (dob) =>
  dob && moment(dob).isValid() ? moment().diff(moment(dob), "years") : null;

// form1.diagnosis is the ICD picker output, [{ code, name }]. Older records only
// carry form1.knownIllness. Same precedence the face sheet uses.
const diagnosisList = (clientData) => {
  const diagnosis = clientData?.form1?.diagnosis;
  if (Array.isArray(diagnosis) && diagnosis.length) {
    return diagnosis
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean);
  }

  const illness = clientData?.form1?.knownIllness;
  if (Array.isArray(illness) && illness.length) {
    return illness
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.illness || item?.contagious || item?.name
      )
      .filter(Boolean);
  }

  return [];
};

const medicationList = (clientData) =>
  (Array.isArray(clientData?.form3) ? clientData.form3 : [])
    .filter((med) => med && !med.discontinued)
    .map((med) => ({
      name: med?.medicationName || "",
      strength:
        med?.strengthAndForm ||
        [med?.dosage, med?.unit].filter(Boolean).join(" ") ||
        "",
      dose:
        [med?.medicationadministered || med?.dosage, med?.unit]
          .filter(Boolean)
          .join(" ") || "",
      frequency: med?.frequency || "",
      narcotic:
        med?.isNarcotic === true ||
        `${med?.isNarcotic || ""}`.toLowerCase() === "yes",
    }))
    .filter((med) => med.name);

// Pull an identification number off form2.insurance by payer keyword. Medicaid
// and Medicare are not their own insurance types on the client record, so match
// against whatever type/provider text the org actually entered.
const insuranceId = (clientData, keywords) => {
  const rows = Array.isArray(clientData?.form2?.insurance)
    ? clientData.form2.insurance
    : [];
  const match = rows.find((row) => {
    const haystack = `${row?.type || ""} ${row?.insuranceType || ""} ${
      row?.providerName || ""
    }`.toLowerCase();
    return keywords.some((keyword) => haystack.includes(keyword));
  });
  if (!match) return "";
  return [match?.providerName, match?.identificationNumber]
    .filter(Boolean)
    .join(" ");
};

export const defaultMedicaidId = (clientData) => insuranceId(clientData, ["medicaid"]);

export const defaultOtherPayerId = (clientData) => {
  const medicare = insuranceId(clientData, ["medicare"]);
  if (medicare) return medicare;

  const rows = Array.isArray(clientData?.form2?.insurance)
    ? clientData.form2.insurance
    : [];
  const medical = rows.find(
    (row) =>
      `${row?.type || ""}`.toLowerCase() === "medical" &&
      (row?.providerName || row?.identificationNumber)
  );
  return medical
    ? [medical?.providerName, medical?.identificationNumber].filter(Boolean).join(" ")
    : "";
};

// The facility the client is assigned to. Patient.facilityId is a populated
// ref array, but older records carry a single populated object, and a response
// that skipped the populate leaves a bare id string. Only an object with
// facility fields on it counts.
const assignedFacility = (clientDetails) => {
  const assigned = clientDetails?.facilityId;
  const candidates = Array.isArray(assigned) ? assigned : [assigned];
  const match = candidates.find(
    (item) => item && typeof item === "object" && (item.name || item.address)
  );
  return match || clientDetails?.facility || null;
};

export const resolveOrg = (clientDetails, profile) => {
  const companyName =
    profile?.business?.companyName ||
    clientDetails?.business?.companyName ||
    clientDetails?.companyName ||
    "";

  // The sheet identifies the sending facility, not the parent business. The
  // crew was dispatched to a building and the receiving hospital calls that
  // building back, so the client's own facility wins over the org name, which
  // is only a last resort for a client with no facility assigned.
  const facility = assignedFacility(clientDetails) || profile?.facility || {};
  const facilityName = facility?.name || companyName;
  // Facility records store zipcode/phone; the older shape used zipCode and
  // phoneNumber. Both are read so neither prints as a gap.
  const facilityAddress = joinParts(
    facility?.address,
    facility?.addressLineTwo || facility?.address2,
    facility?.city,
    facility?.state,
    facility?.zipcode || facility?.zipCode
  );
  const facilityPhone =
    facility?.phone ||
    facility?.phoneNumber ||
    profile?.business?.phoneNumber ||
    "";
  const facilityLicense =
    facility?.providerLicense || profile?.facility?.providerLicense || "";

  return { companyName, facilityName, facilityAddress, facilityPhone, facilityLicense };
};

export const defaultAllergies = (clientDetails) =>
  (clientDetails?.form1?.knownAllergies || [])
    .map((item) => (typeof item === "string" ? item : item?.name || item?.allergy))
    .filter(Boolean);

export const emsRecordId = (clientDetails) => {
  const raw = `${clientDetails?.id || ""}`.slice(-6).toUpperCase();
  return raw ? `OG-${raw}` : EMPTY;
};

/**
 * The one shape the drawer and the printed sheet both read. Everything the sheet
 * shows is derived here so screen and print output cannot drift apart.
 */
export const buildEmsSheetData = ({ clientDetails, profile, formData }) => {
  const form = formData || {};
  const org = resolveOrg(clientDetails, profile);
  const allergies = defaultAllergies(clientDetails);
  const dob = clientDetails?.form1?.dob;
  const dobAge = age(dob);
  const reviewedOn = formatDate(form.completedAt) || formatDate(new Date());

  const codeStatus = form.codeStatus || NOT_DOCUMENTED;
  const codeDocumented = codeStatus !== NOT_DOCUMENTED;
  const codeNote = codeDocumented
    ? joinParts(
        form.directiveSignedOn ? `Signed ${formatDate(form.directiveSignedOn)}` : "",
        form.directiveLocation || ""
      ) || "Confirm the original form travels with the client."
    : "No advance directive on file. Treat as full code until confirmed.";

  // An empty allergy list is never printed as "None". Only the explicit
  // "No known allergies" attestation is allowed to say that.
  const allergyValue = form.noKnownAllergies
    ? "No known allergies"
    : allergies.join(" / ") || NOT_DOCUMENTED;
  const allergyNote = form.noKnownAllergies
    ? `Recorded as none by ${form.completedBy || "staff"}`
    : allergies.length
      ? form.allergiesReviewed
        ? `Last reviewed ${reviewedOn}`
        : "Not reviewed at transfer. Confirm against the record."
      : "Blank in chart. Do not read as no known allergies.";

  const isolation = form.isolationStatus || NOT_DOCUMENTED;

  return {
    org,
    recordId: emsRecordId(clientDetails),
    printedAt: moment().format(DATE_TIME_FORMAT),

    client: {
      name: clientDetails?.name || "",
      initials: getInitials(clientDetails?.name),
      sex: clientDetails?.form1?.gender || "",
      dob: formatDate(dob),
      dobWithAge: formatDate(dob)
        ? `${formatDate(dob)}${dobAge != null ? ` (${dobAge})` : ""}`
        : "",
      weight: form.weight ? `${form.weight} lb` : "",
      room: form.room || clientDetails?.form1?.roomNumber || "",
      admitted: formatDate(clientDetails?.form1?.admissionDate),
    },

    critical: {
      codeStatus,
      codeDocumented,
      codeNote,
      allergyValue,
      allergyDocumented: form.noKnownAllergies || allergies.length > 0,
      allergyNote,
      isolation,
      isolationDocumented: isolation !== NOT_DOCUMENTED,
      isolationNote:
        isolation === NOT_DOCUMENTED
          ? "Not assessed. Use standard precautions."
          : isolation === "Not assessed"
            ? "Use standard precautions until assessed."
            : "Standard precautions",
    },

    diagnoses: diagnosisList(clientDetails),
    medications: medicationList(clientDetails),
    medicationsVerified: !!form.medicationsVerified,
    medicationFootnote: form.medicationsVerified
      ? `Full MAR and discontinued medications attached. Verified against MAR ${reviewedOn}.`
      : "MAR not reconciled at transfer. Crew should confirm the full list with the pharmacy.",
    devices: DEVICE_OPTIONS.filter((label) => form.deviceSelections?.[label]),

    contacts: [
      {
        label: "Authorized rep / POA",
        value:
          form.authorizedRepresentative ||
          clientDetails?.form2?.emergencyContactName ||
          "",
      },
      {
        label: "Rep phone",
        value: clientDetails?.form2?.emergencyContactPhoneNumber || "",
      },
      { label: "Primary care physician", value: clientDetails?.form2?.pcpName || "" },
      {
        label: "Physician phone",
        value:
          clientDetails?.form2?.pcpPhoneNumber || clientDetails?.form2?.pcpEmail || "",
      },
      { label: "Case manager", value: clientDetails?.form2?.caseManagerName || "" },
      {
        label: "Case manager cell",
        value: clientDetails?.form2?.caseManagerPhoneNumber || "",
      },
    ],

    coverage: [
      { label: "Medicaid ID", value: form.medicaidId || "" },
      { label: "Medicare / other", value: form.otherPayerId || "" },
      { label: "Pharmacy", value: clientDetails?.form2?.pharmacyName || "" },
      {
        label: "Pharmacy phone",
        value: clientDetails?.form2?.pharmacyPhoneNumber || "",
      },
    ],

    facility: [
      { label: "Facility", value: org.facilityName },
      { label: "License", value: form.facilityLicense || org.facilityLicense },
      { label: "Address", value: org.facilityAddress },
      { label: "24 hour phone", value: form.facilityPhone || org.facilityPhone },
    ],

    attachments: ATTACHMENT_OPTIONS.map((item) => ({
      label: item.label,
      checked: !!form.attachmentChecks?.[item.key],
    })),

    scene: {
      reason: form.emergencyNotes || "",
      times: TIME_FIELDS.map((field) => ({
        label: field.label,
        value: formatClock(form.times?.[field.key]),
      })),
      vitals: VITAL_FIELDS.map((field) => ({
        label: field.short,
        value: form.vitals?.[field.key] || "",
      })),
      notTransported: !!form.didNotGoToHospital,
      hospital: form.didNotGoToHospital ? "" : form.hospitalDestination || "",
      transportedBy: form.didNotGoToHospital ? "" : form.transportedBy || "",
      repNotified: !!form.authorizedRepNotified,
      representative: form.authorizedRepresentative || "",
      contactedBy: form.authorizedRepContactedBy || "",
      notifiedAt: formatDateTime(form.notificationDate),
      completedBy: form.completedBy || "",
      completedAt: formatDateTime(form.completedAt),
    },
  };
};
